import { useEffect, useState } from "react";
import { Lock, X } from "lucide-react";
import {
  OAUTH2_AUTH_TYPE,
  emptyOAuthConfig,
  buildOAuthConfigFromForm,
  validateOAuthConfig,
  isAuthedWebhook,
  isAdminUser,
  resolveAuthTypes
} from "./webhookHelpers";
import {
  WebhookOAuthFields,
  getOAuthRequiredFields,
  isOAuthAuthType
} from "./WebhookOAuthFields";
import { inputBaseClass, btnPrimaryClass, btnSecondaryClass } from "./constants";
import { FormLabel, SelectField } from "./shared";

function StatusToggle({ isActive, onChange }) {
  return (
    <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
          isActive
            ? "bg-green-50 text-green-700 border border-green-200 shadow-sm"
            : "text-gray-600 hover:text-gray-800"
        }`}
      >
        <span
          className={`w-2 h-2 rounded-full ${isActive ? "bg-green-500" : "bg-gray-300"}`}
          aria-hidden
        />
        Active
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
          !isActive
            ? "bg-white text-gray-900 border border-gray-200 shadow-sm"
            : "text-gray-600 hover:text-gray-800"
        }`}
      >
        Disable
      </button>
    </div>
  );
}

export function EditWebhookModal({
  webhook,
  integrations,
  onClose,
  onSave,
  saving
}) {
  const admin = isAdminUser();
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [authType, setAuthType] = useState("none");
  const [isActive, setIsActive] = useState(true);
  const [oauthConfig, setOauthConfig] = useState(emptyOAuthConfig());
  const [rotateCreds, setRotateCreds] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const urlLocked = isAuthedWebhook(webhook);
  const hasStoredCreds = Boolean(webhook?.auth_config?.secret_id);
  const requiredFields = getOAuthRequiredFields(integrations, OAUTH2_AUTH_TYPE);
  const authTypes = resolveAuthTypes(integrations);

  useEffect(() => {
    if (!webhook) return;
    setName(webhook.name ?? "");
    setUrl(webhook.webhook_url ?? "");
    setAuthType(webhook.auth_type || "none");
    setIsActive(webhook.is_active !== false);
    setOauthConfig(emptyOAuthConfig());
    setRotateCreds(false);
    setFieldErrors({});
  }, [webhook]);

  if (!webhook) return null;

  const oauthFormConfig = {
    ...oauthConfig,
    token_url: oauthConfig.token_url || webhook.auth_config?.token_url || "",
    scopes: oauthConfig.scopes || webhook.auth_config?.scopes || ""
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});

    const payload = {};
    const trimmedName = name.trim();
    if (trimmedName && trimmedName !== (webhook.name ?? "")) payload.name = trimmedName;
    if (isActive !== (webhook.is_active !== false)) payload.is_active = isActive;

    if (!urlLocked) {
      const trimmedUrl = url.trim();
      if (trimmedUrl && trimmedUrl !== (webhook.webhook_url ?? "")) {
        payload.webhook_url = trimmedUrl;
      }
    }

    if (admin) {
      const prevAuth = webhook.auth_type || "none";

      if (authType !== prevAuth) {
        payload.auth_type = authType;
        if (authType === "none") {
          payload.auth_config = null;
        } else if (isOAuthAuthType(authType)) {
          const v = validateOAuthConfig(oauthConfig, requiredFields);
          if (Object.keys(v).length) {
            setFieldErrors(v);
            return;
          }
          payload.auth_config = buildOAuthConfigFromForm(oauthConfig);
        }
      } else if (isOAuthAuthType(authType)) {
        const prev = webhook.auth_config || {};
        const authCfg = {};
        const tokenUrl = oauthFormConfig.token_url?.trim() ?? "";
        const scopes = oauthFormConfig.scopes?.trim() ?? "";

        if (tokenUrl && tokenUrl !== (prev.token_url ?? "")) authCfg.token_url = tokenUrl;
        if (scopes && scopes !== (prev.scopes ?? "")) authCfg.scopes = scopes;

        if (rotateCreds) {
          const v = {};
          if (!oauthConfig.client_id?.trim()) v.client_id = "Required";
          if (!oauthConfig.client_secret?.trim()) v.client_secret = "Required";
          if (Object.keys(v).length) {
            setFieldErrors(v);
            return;
          }
          authCfg.client_id = oauthConfig.client_id.trim();
          authCfg.client_secret = oauthConfig.client_secret.trim();
        }

        if (Object.keys(authCfg).length) payload.auth_config = authCfg;
      }
    }

    if (!Object.keys(payload).length) {
      onClose();
      return;
    }

    await onSave(webhook.id, payload, setFieldErrors);
  };

  return (
    <div className="fixed inset-0 z-[900] flex items-center justify-center p-4 bg-black/40">
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-labelledby="edit-webhook-title"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 id="edit-webhook-title" className="text-lg font-semibold text-gray-900">
            Edit Webhook
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          <div>
            <FormLabel htmlFor="edit-wh-name">Webhook Name</FormLabel>
            <input
              id="edit-wh-name"
              className={`${inputBaseClass} ${fieldErrors.name ? "border-red-400" : ""}`}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            {fieldErrors.name ? (
              <p className="text-[11px] text-red-600 mt-1">{fieldErrors.name}</p>
            ) : null}
          </div>

          <div>
            <FormLabel htmlFor="edit-wh-url">
              Webhook URL{" "}
              {urlLocked ? (
                <span className="inline-flex items-center gap-1 text-gray-500 font-normal">
                  <Lock className="w-3 h-3" aria-hidden />
                  Locked
                </span>
              ) : null}
            </FormLabel>
            <input
              id="edit-wh-url"
              className={`${inputBaseClass} ${urlLocked ? "bg-gray-100 text-gray-600 cursor-not-allowed" : ""} ${fieldErrors.webhook_url ? "border-red-400" : ""}`}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              readOnly={urlLocked}
              disabled={urlLocked}
            />
            {fieldErrors.webhook_url ? (
              <p className="text-[11px] text-red-600 mt-1">{fieldErrors.webhook_url}</p>
            ) : null}
            {urlLocked ? (
              <p className="text-[11px] text-gray-500 mt-2 leading-relaxed italic">
                Authenticated webhooks can&apos;t have their URL changed. To send to a different
                destination, create a new webhook and delete this one.{" "}
                <span className="not-italic">
                  (This is a server-side rule, not just UI — PATCH attempting to change the URL
                  returns 409.)
                </span>
              </p>
            ) : null}
          </div>

          {admin ? (
            <>
              <div>
                <FormLabel htmlFor="edit-wh-auth">Authentication</FormLabel>
                <SelectField
                  id="edit-wh-auth"
                  value={authType}
                  onChange={(e) => {
                    setAuthType(e.target.value);
                    setRotateCreds(false);
                    setOauthConfig(emptyOAuthConfig());
                  }}
                >
                  {authTypes.map((a) => (
                    <option key={a.value} value={a.value}>
                      {a.label}
                    </option>
                  ))}
                </SelectField>
                {isAuthedWebhook(webhook) ? (
                  <p className="text-[11px] text-gray-500 mt-2">
                    Changing to &quot;None&quot; will delete the stored credentials in Secret
                    Manager.
                  </p>
                ) : null}
                {fieldErrors.auth_type ? (
                  <p className="text-[11px] text-red-600 mt-1">{fieldErrors.auth_type}</p>
                ) : null}
              </div>

              {isOAuthAuthType(authType) ? (
                <WebhookOAuthFields
                  mode="edit"
                  config={oauthFormConfig}
                  onChange={setOauthConfig}
                  errors={fieldErrors}
                  requiredFields={requiredFields}
                  hasStoredCreds={hasStoredCreds || isAuthedWebhook(webhook)}
                  rotateCreds={rotateCreds}
                  onRotateToggle={(v) => {
                    setRotateCreds(v);
                    if (!v) {
                      setOauthConfig((c) => ({
                        ...c,
                        client_id: "",
                        client_secret: ""
                      }));
                    }
                  }}
                />
              ) : null}
            </>
          ) : null}

          <div>
            <FormLabel>Status</FormLabel>
            <div className="mt-2">
              <StatusToggle isActive={isActive} onChange={setIsActive} />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <button type="button" className={btnSecondaryClass} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={btnPrimaryClass} disabled={saving}>
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
